/* here&found — <hf-main-product> (assets/hf-main-product.js).
   The PDP section root (sections/hf-main-product.liquid). Pure enhancement —
   with JS off the page still sells: the buy form posts natively, the native
   variant <select name="id"> picks variants, the gallery swipes on CSS
   scroll-snap and thumbnails/dots are real fragment links.

   With JS on, this element:
   - marks itself [data-js] (+ [data-has-picker]) so the native select hides
     and the hf-variant-picker swatches take over;
   - resolves the picked option combination against the embedded variant JSON
     and syncs every [data-hf-id] control (buy form, sticky bar), the price
     displays (.hf-priceblk + % off badge / [data-hf-bar-price|compare]), the
     add-button state, the picker's "Option: Value" legend and the ?variant=
     URL param;
   - PACK TIERS: a checked tier selects a real Shopify `Pack` variant. The
     variant owns checkout price, compare-at price and fulfillment identity;
     cart quantity stays 1. No storefront-only discount arithmetic;
   - upgrades gallery thumbnails AND mobile dots to smooth scroll, tracks the
     current slide into the thumbs, dots and `i / count` counter; jumps to a
     variant's featured image on variant change (by media position, so the
     video slot offset never desyncs it).

   prefers-reduced-motion → instant gallery scroll. All listeners are removed
   on disconnect via one AbortController. No framework. */

class HFMainProduct extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.setAttribute('data-js', '');

    // --- variant data ---------------------------------------------------
    this.picker = this.querySelector('hf-variant-picker');
    this.variants = [];
    if (this.picker) {
      this.setAttribute('data-has-picker', '');
      const json = this.picker.querySelector('[data-hf-variants]');
      try {
        this.variants = JSON.parse(json ? json.textContent : '[]');
      } catch (e) {
        this.variants = [];
      }
    }
    this.packPosition = this.picker
      ? parseInt(this.picker.getAttribute('data-hf-pack-position'), 10) || 0
      : 0;
    this.currentVariant = this.variants.length
      ? this.variants.find((v) => {
          const input = this.querySelector('[data-hf-id]');
          return input && String(v.id) === String(input.value);
        }) || this.variants[0]
      : null;

    // --- reveal enhancement-only controls -------------------------------
    this.querySelectorAll('[data-hf-qty]').forEach((btn) => btn.removeAttribute('hidden'));
    this.querySelectorAll('[data-hf-pack-tier]').forEach((tier) => tier.removeAttribute('hidden'));
    this.querySelectorAll('[data-hf-related-block]').forEach((block) => block.removeAttribute('hidden'));

    // --- delegated listeners --------------------------------------------
    this.addEventListener('change', (event) => {
      const opt = event.target.closest('.hf-vp__input');
      if (opt) return this.onOptionChange();
      const tier = event.target.closest('.hf-tier__input');
      if (tier && tier.checked) return this.applyTier(tier);
      const natsel = event.target.closest('select[data-hf-id]');
      if (natsel) return this.onNativeSelect(natsel);
    }, { signal });

    this.addEventListener('click', (event) => {
      const qty = event.target.closest('[data-hf-qty]');
      if (qty) return this.stepQuantity(qty);
      const thumb = event.target.closest('[data-hf-thumb]');
      if (thumb) {
        event.preventDefault();
        return this.goToSlide(parseInt(thumb.getAttribute('data-hf-thumb'), 10));
      }
    }, { signal });

    // --- gallery current-slide tracking ---------------------------------
    this.track = this.querySelector('[data-hf-gallery] [data-rail-track]');
    if (this.track) {
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.syncThumbs();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    }

    // --- initial tier sync (a preselected tier owns qty + bar price) ----
    const checkedTier = this.querySelector('.hf-tier__input:checked');
    if (checkedTier) this.applyTier(checkedTier);
  }

  disconnectedCallback() {
    this.ac.abort();
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  // --- variants ---------------------------------------------------------

  onOptionChange() {
    if (!this.variants.length) return;
    const picked = this.currentVariant && Array.isArray(this.currentVariant.options)
      ? this.currentVariant.options.slice()
      : [];
    this.picker.querySelectorAll('.hf-vp__input:checked').forEach((input) => {
      picked[parseInt(input.getAttribute('data-hf-option-position'), 10) - 1] = input.value;
      const legend = input.closest('.hf-vp__opt');
      const current = legend && legend.querySelector('[data-hf-opt-current]');
      if (current) current.textContent = input.value;
    });
    const tier = this.querySelector('.hf-tier__input:checked');
    if (tier && this.packPosition) picked[this.packPosition - 1] = tier.getAttribute('data-pack-value');
    const variant = this.variants.find((v) => picked.every((value, i) => value === undefined || v.options[i] === value));
    if (variant) this.applyVariant(variant, true);
  }

  onNativeSelect(select) {
    const variant = this.variants.find((v) => String(v.id) === String(select.value));
    if (variant) this.applyVariant(variant, true);
  }

  applyVariant(variant, updateUrl) {
    this.currentVariant = variant;
    this.applyId(variant.id);
    this.renderPrice(variant);
    this.renderAvailability(variant);
    this.renderTiers(variant);
    this.jumpToVariantImage(variant);
    if (updateUrl && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(null, '', url.toString());
    }
  }

  applyId(id) {
    if (!id) return;
    this.querySelectorAll('[data-hf-id]').forEach((el) => {
      el.value = String(id);
    });
  }

  renderPrice(variant) {
    // Structure only. The NUMBERS come from renderSelectedPrice(), so the
    // headline price, its struck compare-at and the "% off" badge always show
    // what the shopper is actually buying — the SELECTED bundle tier, not the
    // bare unit price (SN PDP feedback item 1).
    const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    this.querySelectorAll('.hf-priceblk').forEach((blk) => {
      if (!hasCompare || blk.querySelector('s')) return;
      const struck = document.createElement('s');
      struck.className = 'hf-tnum';
      blk.insertBefore(struck, blk.querySelector('[data-hf-off]'));
    });
    this.renderSelectedPrice();
  }

  renderAvailability(variant) {
    this.querySelectorAll('[data-hf-add]').forEach((btn) => {
      btn.disabled = !variant.available;
      const label = btn.querySelector('[data-hf-add-label]');
      if (label) {
        label.textContent = variant.available
          ? btn.getAttribute('data-add-label') || label.textContent
          : btn.getAttribute('data-soldout-label') || label.textContent;
      }
    });
  }

  // --- pack variants ----------------------------------------------------

  variantForPack(packValue, fromVariant = this.currentVariant) {
    if (!packValue || !this.packPosition || !this.variants.length) return null;
    const options = fromVariant && Array.isArray(fromVariant.options)
      ? fromVariant.options.slice()
      : this.variants[0].options.slice();
    options[this.packPosition - 1] = packValue;
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === options[index])) || null;
  }

  renderTiers(variant) {
    this.querySelectorAll('.hf-tier__input').forEach((input) => {
      const packVariant = this.variantForPack(input.getAttribute('data-pack-value'), variant);
      const box = input.closest('.hf-tier');
      const main = box && box.querySelector('[data-hf-tier-price]');
      const struck = box && box.querySelector('[data-hf-tier-compare]');
      input.disabled = !packVariant || !packVariant.available;
      if (box) box.toggleAttribute('data-unavailable', input.disabled);
      if (!packVariant) return;
      input.setAttribute('data-variant-id', String(packVariant.id));
      if (main) main.textContent = this.formatMoney(packVariant.price);
      if (struck) {
        if (packVariant.compare_at_price > packVariant.price) {
          struck.textContent = this.formatMoney(packVariant.compare_at_price);
          struck.removeAttribute('hidden');
        } else {
          struck.setAttribute('hidden', '');
        }
      }
    });
    this.renderSelectedPrice();
  }

  applyTier(input) {
    const variant = this.variantForPack(input.getAttribute('data-pack-value'));
    if (!variant) return;
    this.querySelectorAll('[data-hf-qty-input], input[name="quantity"]').forEach((el) => {
      el.value = 1;
    });
    this.applyVariant(variant, true);
  }

  // --- the one price the shopper is actually buying ----------------------
  // The headline price block, its "% off" badge and the mobile sticky bar all
  // read the selected Shopify variant, including its real Pack price.

  renderSelectedPrice() {
    const mains = this.querySelectorAll('.hf-priceblk--pdp');
    const barPrice = this.querySelector('[data-hf-bar-price]');
    const barCompare = this.querySelector('[data-hf-bar-compare]');
    const variant = this.currentVariant;
    if (!variant) return;
    const price = variant.price;
    const compare = variant.compare_at_price > variant.price ? variant.compare_at_price : null;
    const priceText = this.formatMoney(price);
    const compareText = compare ? this.formatMoney(compare) : null;
    const pct = compare ? Math.round((1 - price / compare) * 100) : 0;

    if (barPrice) barPrice.textContent = priceText;
    if (barCompare) {
      if (compareText) {
        barCompare.textContent = compareText;
        barCompare.removeAttribute('hidden');
      } else {
        barCompare.setAttribute('hidden', '');
      }
    }
    mains.forEach((blk) => this.writePriceBlock(blk, priceText, compareText, pct));
  }

  // pct omitted => leave the badge exactly as Liquid rendered it
  writePriceBlock(blk, priceText, compareText, pct) {
    const main = blk.querySelector('b');
    if (main) main.textContent = priceText;
    const struck = blk.querySelector('s');
    if (struck) {
      if (compareText) {
        struck.textContent = compareText;
        struck.removeAttribute('hidden');
      } else {
        struck.setAttribute('hidden', '');
      }
    }
    const off = blk.querySelector('[data-hf-off]');
    if (off && pct !== undefined) {
      if (pct > 0) {
        off.textContent = `${pct}% off`;
        off.removeAttribute('hidden');
      } else {
        off.setAttribute('hidden', '');
      }
    }
  }

  // JS twin of snippets/hf-money.liquid — keep the two in step or the price
  // will change the instant a shopper touches a swatch or a tier.
  // ui_kits/pdp/PDPSections.jsx: inr = n => "₹" + Math.round(n).toLocaleString("en-IN")
  // The shop's own money_format ("Rs. {{ amount }}") is deliberately NOT used
  // here: "Rs. 6,900.00" wraps the headline price and clips the struck
  // compare-at in the sticky bar. Checkout keeps the shop format.
  formatMoney(cents) {
    const rupees = Math.round((Number(cents) || 0) / 100);
    return `₹${rupees.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }

  // --- quantity ---------------------------------------------------------

  stepQuantity(btn) {
    const wrap = btn.closest('.hf-buy__qty');
    const input = wrap && wrap.querySelector('input[name="quantity"]');
    if (!input) return;
    const delta = parseInt(btn.getAttribute('data-hf-qty'), 10) || 0;
    const min = parseInt(input.min, 10) || 1;
    const next = Math.max(min, (parseInt(input.value, 10) || min) + delta);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // --- gallery ----------------------------------------------------------

  goToSlide(index) {
    if (!this.track) return;
    const slide = this.track.querySelector(`[data-media-slide="${index}"]`);
    if (!slide) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  jumpToVariantImage(variant) {
    if (!this.track || !variant.featured_image || !variant.featured_image.position) return;
    // media POSITION, not slide index — the video slot shifts indices
    const slide = this.track.querySelector(`[data-media-position="${variant.featured_image.position}"]`);
    if (!slide) return;
    this.goToSlide(parseInt(slide.getAttribute('data-media-slide'), 10));
  }

  syncThumbs() {
    if (!this.track) return;
    const slide = this.track.firstElementChild;
    if (!slide) return;
    const width = slide.getBoundingClientRect().width || 1;
    const index = Math.round(this.track.scrollLeft / width);
    this.querySelectorAll('[data-hf-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-current', parseInt(thumb.getAttribute('data-hf-thumb'), 10) === index);
    });
    const counter = this.querySelector('[data-hf-counter]');
    if (counter) counter.textContent = index + 1;
  }
}

if (!customElements.get('hf-main-product')) customElements.define('hf-main-product', HFMainProduct);
